import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Container,
  Chip,
  Checkbox,
  ListItemText,
  Autocomplete,
  TextField,
  Paper,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import { PredictionRequest, PredictionResponse } from '../types/predictions';
import { PredictionResults } from '../components/PredictionResults';
import { getTracks, getYears, getPredictions, getLatestDataInfo, LatestDataInfo } from '../services/api';
import { TableChart, Info, Speed, DirectionsCar } from '@mui/icons-material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_SELECTED_TRACKS = 10;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const Predictions: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tracks, setTracks] = useState<{ name: string }[]>([]);
  const [years, setYears] = useState<{ year: number }[]>([]);
  const [selectedTracks, setSelectedTracks] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [predictions, setPredictions] = useState<PredictionResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [latestDataInfo, setLatestDataInfo] = useState<LatestDataInfo | null>(null);

  // Helper to get the most recent year
  const mostRecentYear = years.length > 0 ? years[0].year : new Date().getFullYear();

  // Add 'All Tracks' option
  const allTrackNames = tracks.map(track => track.name);
  const trackOptions = ['All Tracks', ...allTrackNames];

  // Effect: At max track count, force year to most recent
  useEffect(() => {
    if (selectedTracks.length >= MAX_SELECTED_TRACKS && selectedYear !== mostRecentYear) {
      setSelectedYear(mostRecentYear);
    }
  }, [selectedTracks, mostRecentYear, selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tracksData, yearsData, latestData] = await Promise.all([
          getTracks(),
          getYears(),
          getLatestDataInfo(),
        ]);
        setTracks(tracksData);
        setYears(yearsData);
        setLatestDataInfo(latestData);
        // Set default year to most recent
        if (yearsData.length > 0) setSelectedYear(yearsData[0].year);
      } catch (err) {
        setError('Failed to load tracks and years');
        console.error('Error loading initial data:', err);
      }
    };
    fetchData();
  }, []);

  const capTrackSelection = (tracks: string[]) => tracks.slice(0, MAX_SELECTED_TRACKS);

  const handleTrackChange = (event: any, newValue: string[]) => {
    if (newValue.includes('All Tracks')) {
      setSelectedTracks(capTrackSelection(allTrackNames));
    } else if (
      selectedTracks.length === allTrackNames.length &&
      !newValue.includes('All Tracks')
    ) {
      setSelectedTracks(capTrackSelection(newValue.filter((v) => v !== 'All Tracks')));
    } else {
      setSelectedTracks(capTrackSelection(newValue));
    }
  };

  const handleYearChange = (e: any) => {
    if (selectedTracks.length >= MAX_SELECTED_TRACKS) return;
    setSelectedYear(Number(e.target.value));
  };

  const handlePredict = async () => {
    if (selectedTracks.length === 0 || !selectedYear) {
      setError('Please select at least one track and year');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const request: PredictionRequest = {
        trackNames: selectedTracks,
        year: selectedTracks.length >= MAX_SELECTED_TRACKS ? mostRecentYear : selectedYear,
      };
      const results = await getPredictions(request);
      setPredictions(results);
    } catch (err) {
      setError('Failed to load loop data');
      console.error('Error generating predictions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, px: isMobile ? 1 : 3 }}>
      {latestDataInfo && (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            mb: 3, 
            backgroundColor: (theme) => 
              theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.02)',
            border: (theme) => 
              `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.12)' 
                : 'rgba(0, 0, 0, 0.12)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              Latest data from <strong>{latestDataInfo.track}</strong> on{' '}
              <strong>{new Date(latestDataInfo.latest_date).toLocaleDateString()}</strong>{' '}
              (#{latestDataInfo.race_number})
            </Typography>
          </Box>
        </Paper>
      )}

      <Box sx={{ mb: 4 }}>
        <Stack 
          direction={isMobile ? "column" : "row"} 
          spacing={2} 
          alignItems={isMobile ? "stretch" : "center"}
        >
          <FormControl sx={{ flex: isMobile ? 1 : 2 }}>
            <Autocomplete
              multiple
              id="track-select"
              options={trackOptions}
              value={
                selectedTracks.length === allTrackNames.length
                  ? allTrackNames
                  : selectedTracks
              }
              onChange={handleTrackChange}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={
                      option === 'All Tracks'
                        ? selectedTracks.length === allTrackNames.length
                        : selectedTracks.indexOf(option) > -1
                    }
                  />
                  <ListItemText
                    primary={option}
                    primaryTypographyProps={{
                      fontWeight:
                        option === 'All Tracks' ? 'bold' : 'normal',
                    }}
                  />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Track"
                  placeholder="Search tracks..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                  }}
                />
              )}
              renderTags={(value: string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    sx={{
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                      color: 'text.primary',
                      '& .MuiChip-deleteIcon': {
                        color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        '&:hover': {
                          color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                      },
                    }}
                  />
                ))
              }
              sx={{
                '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                },
              }}
            />
          </FormControl>

          <FormControl sx={{ flex: isMobile ? 1 : 1 }}>
            <InputLabel 
              id="year-select-label"
              sx={{
                '&.Mui-focused': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
            >
              Year
            </InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
              disabled={selectedTracks.length >= MAX_SELECTED_TRACKS}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '& .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                },
                '&:hover .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
              MenuProps={MenuProps}
            >
              {years.map((year) => (
                <MenuItem key={year.year} value={year.year}>
                  {year.year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={handlePredict}
            disabled={selectedTracks.length === 0 || isLoading}
            sx={{ 
              height: '56px', 
              flex: isMobile ? 1 : 1,
              backgroundColor: 'background.paper',
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
            }}
            startIcon={isLoading ? undefined : <TableChart />}
          >
            {isLoading ? <CircularProgress size={24} thickness={4} sx={{ 
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000'
            }} /> : 'Load loop data'}
          </Button>
        </Stack>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setSelectedTracks([
              'Austin',
              'Charlotte (Road)',
              'Chicago',
              'Daytona (Road)',
              'Elkhart Lake',
              'Indianapolis G.P.',
              'Sonoma',
              'Watkins Glen',
              'Mexico City',
              'San Diego'
            ])}
            startIcon={<DirectionsCar />}
            sx={{ 
              height: '40px',
              backgroundColor: 'background.paper',
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              borderRadius: '20px',
              lineHeight: isMobile ? 1.2 : 'normal',
              '&:hover': {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
            }}
          >
            Road Courses
          </Button>

          <Button
            variant="outlined"
            onClick={() => setSelectedTracks([
              'Atlanta',
              'Daytona',
              'Talladega'
            ])}
            startIcon={<Speed />}
            sx={{ 
              height: '40px',
              backgroundColor: 'background.paper',
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              borderRadius: '20px',
              lineHeight: isMobile ? 1.2 : 'normal',
              '&:hover': {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
            }}
          >
            Drafting Tracks
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {predictions.length > 0 && (
        <PredictionResults predictions={predictions} isLoading={isLoading} />
      )}
    </Container>
  );
}; 